// Custom hook for encryption/decryption with logging
import { useLogs } from "../context/LogContext";
import CryptoJS from "crypto-js";

// NOTE: These MUST match the Android `BuildConfig.PASSWORD` and `BuildConfig.SALT`
// used by `SecurityUtils` (PBKDF2WithHmacSHA256, 65536 iterations, 256-bit key).
const PASSWORD = "ByoSyncPayWithFace";
const SALT = "ByoSync";

const key = CryptoJS.PBKDF2(PASSWORD, CryptoJS.enc.Utf8.parse(SALT), {
  // CryptoJS keySize is in 32-bit words. 256-bit / 32 = 8 words.
  keySize: 256 / 32,
  iterations: 65536,
  hasher: CryptoJS.algo.SHA256,
});

export function useEncryptDecrypt() {
  const { addLog } = useLogs(); // Use hook inside the custom hook

  // Android-compatible: returns "ivHex:dataHex" (AES/CBC/PKCS5Padding; PKCS5==PKCS7 here)
  const encrypt = (text) => {
    addLog(`Encrypted the data ${text}`);

    const iv = CryptoJS.lib.WordArray.random(16); // 16 bytes IV

    // Encrypt the text
    const encrypted = CryptoJS.AES.encrypt(text, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    const ivHex = iv.toString(CryptoJS.enc.Hex);
    const dataHex = encrypted.ciphertext.toString(CryptoJS.enc.Hex);
    const out = `${ivHex}:${dataHex}`;

    addLog(`Encrypted the data is ${out}`);
    return out;
  };

  // decrypt(encryptedData, { silent: true }) -> no logs (useful for UI pages to avoid log spam)
  // Supports BOTH formats:
  // - New Android format: "ivHex:dataHex"
  // - Backward-compatible legacy web format: "ivBase64:dataBase64"
  const decrypt = (encryptedData, options = {}) => {
    const { silent = false } = options || {};
    if (encryptedData == null) return null;
    if (typeof encryptedData === "string" && encryptedData.trim() === "") return "";

    if (!silent) addLog(`Decrypted the encrypted data `);
    try {
      const parts = String(encryptedData).split(":");
      if (parts.length !== 2) return null;

      const [part1, part2] = parts;

      // Match Android heuristic: treat as base64 if it contains base64 chars OR isn't hex.
      const isBase64 =
        part1.includes("=") ||
        part1.includes("+") ||
        part1.includes("/") ||
        part2.includes("=") ||
        part2.includes("+") ||
        part2.includes("/") ||
        !/^[0-9a-fA-F]+$/.test(part1);

      const iv = isBase64
        ? CryptoJS.enc.Base64.parse(part1)
        : CryptoJS.enc.Hex.parse(part1);
      const ciphertext = isBase64
        ? CryptoJS.enc.Base64.parse(part2)
        : CryptoJS.enc.Hex.parse(part2);

      const decrypted = CryptoJS.AES.decrypt(
        { ciphertext },
        key,
        { iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
      );

      const plain = decrypted.toString(CryptoJS.enc.Utf8);
      if (!silent) addLog(`Decrypted data is ${plain}`);
      return plain;
    } catch (err) {
      console.error("Decryption error:", err);
      return null;
    }
  };

  return { encrypt, decrypt };
}
